import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  FiCalendar,
  FiChevronDown,
  FiChevronRight,
  FiMenu,
  FiX,
} from "react-icons/fi";
import gsap from "gsap";
import {
  headerNavigation,
  serviceDropdownItems,
} from "../../data/navigation";
import { knownRoutes } from "../../data/routes";
import { toRoutePath } from "../../lib/seo";
import styles from "./Header.module.css";

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const dropdownRef = useRef(null);
  const dropdownItemRefs = useRef([]);
  const drawerRef = useRef(null);
  const backdropRef = useRef(null);
  const mobileLinkRefs = useRef([]);
  const location = useLocation();
  // Routes always render with a trailing slash (see CanonicalPath in App.js),
  // but knownRoutes stores paths without one — normalize before comparing so
  // every real inner page isn't mistaken for a 404.
  const normalizedPathname =
    location.pathname.length > 1
      ? location.pathname.replace(/\/+$/, "")
      : location.pathname;
  const isNotFoundPage = !knownRoutes.includes(normalizedPathname);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsServicesOpen(false);
    setIsMobileServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const drawer = drawerRef.current;
    const backdrop = backdropRef.current;
    const links = mobileLinkRefs.current.filter(Boolean);

    if (!drawer || !backdrop) {
      return;
    }

    gsap.killTweensOf([drawer, backdrop, links]);

    if (isMenuOpen) {
      gsap.set(backdrop, { display: "block" });
      gsap.set(drawer, { display: "block", visibility: "visible" });
      gsap.set(drawer, { xPercent: -100, opacity: 0 });
      gsap.to(backdrop, {
        opacity: 1,
        duration: 0.2,
        ease: "power2.out",
      });
      gsap.to(drawer, {
        xPercent: 0,
        opacity: 1,
        duration: 0.36,
        ease: "power3.out",
      });
      gsap.fromTo(
        links,
        { x: -16, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.24,
          stagger: 0.04,
          delay: 0.08,
          ease: "power2.out",
        }
      );
      return;
    }

    gsap.to(backdrop, {
      opacity: 0,
      duration: 0.18,
      ease: "power2.in",
      onComplete: () => {
        gsap.set(backdrop, { display: "none" });
      },
    });

    gsap.to(drawer, {
      xPercent: -100,
      opacity: 0,
      duration: 0.26,
      ease: "power2.in",
      onComplete: () => {
        gsap.set(drawer, { display: "none", visibility: "hidden" });
      },
    });
  }, [isMenuOpen]);

  useEffect(() => {
    const dropdown = dropdownRef.current;
    const items = dropdownItemRefs.current.filter(Boolean);

    if (!dropdown) {
      return;
    }

    gsap.killTweensOf([dropdown, items]);

    if (isServicesOpen) {
      gsap.set(dropdown, { display: "grid", visibility: "visible" });
      gsap.fromTo(
        dropdown,
        { y: -10, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.24,
          ease: "power2.out",
        }
      );
      gsap.fromTo(
        items,
        { x: -10, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.22,
          stagger: 0.02,
          delay: 0.05,
          ease: "power2.out",
        }
      );
      return;
    }

    gsap.to(dropdown, {
      y: -6,
      opacity: 0,
      duration: 0.18,
      ease: "power2.in",
      onComplete: () => {
        gsap.set(dropdown, { display: "none", visibility: "hidden" });
      },
    });
  }, [isServicesOpen]);

  const openServices = () => {
    if (window.innerWidth >= 992) {
      setIsServicesOpen(true);
    }
  };

  const closeServices = () => {
    if (window.innerWidth >= 992) {
      setIsServicesOpen(false);
    }
  };

  const toggleMobileServices = () => {
    setIsMobileServicesOpen((open) => !open);
  };

  return (
    <header
      className={[
        styles.header,
        isScrolled || isNotFoundPage ? styles.headerScrolled : styles.headerOverlay,
      ].join(" ")}
    >
      <div className={styles.shell}>
        <div className={styles.navBar}>
          <NavLink to="/" className={styles.brand} aria-label="MIT4Doctors home">
            <img
              src="/img/logo/mit-logo-w.webp"
              alt="MIT4Doctors"
              className={styles.brandLogo}
            />
          </NavLink>

          <div className={styles.navArea} onMouseLeave={closeServices}>
            <nav className={styles.navPanel} aria-label="Primary navigation">
              {headerNavigation.map((item) => {
                if (item.label === "Services") {
                  return (
                    <button
                      key={item.label}
                      type="button"
                      className={styles.navTrigger}
                      onClick={() =>
                        setIsServicesOpen((open) => !open)
                      }
                      onMouseEnter={openServices}
                      aria-expanded={isServicesOpen}
                      aria-haspopup="menu"
                    >
                      <span>{item.label}</span>
                      <FiChevronDown aria-hidden="true" />
                    </button>
                  );
                }

                return (
                  <NavLink
                    key={item.to}
                    to={toRoutePath(item.to)}
                    end={item.to === "/"}
                    className={({ isActive }) =>
                      `${styles.navItem} ${isActive ? styles.navItemActive : ""}`
                    }
                  >
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </nav>

            <div
              ref={dropdownRef}
              className={styles.servicesDropdown}
              aria-hidden={!isServicesOpen}
              onMouseEnter={openServices}
              onMouseLeave={closeServices}
            >
              <div className={styles.servicesDropdownGrid}>
                {serviceDropdownItems.map((item, index) => (
                  <div
                    key={item.title}
                    className={styles.serviceRow}
                    ref={(element) => {
                      dropdownItemRefs.current[index] = element;
                    }}
                  >
                    <NavLink to={toRoutePath(item.link)} className={styles.serviceRowMain}>
                      {item.icon ? (
                        <span className={styles.serviceIcon}>
                          <img src={item.icon} alt="" aria-hidden="true" />
                        </span>
                      ) : null}
                      <span className={styles.serviceRowTitle}>{item.title}</span>
                    </NavLink>

                    {item.children?.length ? (
                      <div className={styles.serviceChildren}>
                        {item.children.map((child) => (
                          <NavLink key={child.title} to={toRoutePath(child.link)}>
                            <FiChevronRight aria-hidden="true" />
                            <span>{child.title}</span>
                          </NavLink>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>

              <div className={styles.servicesDropdownAside}>
                <p className={styles.servicesAsideEyebrow}>Not sure where to start?</p>
                <p className={styles.servicesAsideHeading}>Talk to our care team</p>
                <p className={styles.servicesAsideText}>
                  We&apos;ll match you with the right service for your practice or your
                  care needs.
                </p>
                <NavLink to={toRoutePath("/contact-us")} className={`btn btn-dark-outline ${styles.servicesAsideButton}`}>
                  Book a Consultation
                </NavLink>
                <NavLink to={toRoutePath("/services")} className={styles.servicesDropdownTitle}>
                  View All Services
                  <FiChevronRight aria-hidden="true" />
                </NavLink>
              </div>
            </div>
          </div>

          <div className={styles.actions}>
            <NavLink to={toRoutePath("/contact-us")} className={`btn ${styles.appointmentButton}`}>
              <FiCalendar aria-hidden="true" />
              <span>Appointment</span>
            </NavLink>

            <button
              type="button"
              className={styles.menuToggle}
              aria-label="Toggle navigation menu"
              aria-expanded={isMenuOpen}
              onClick={() => setIsMenuOpen((open) => !open)}
            >
              {isMenuOpen ? <FiX size={20} /> : <FiMenu size={20} />}
            </button>
          </div>
        </div>
      </div>

      <button
        ref={backdropRef}
        type="button"
        className={styles.mobileBackdrop}
        aria-label="Close navigation"
        onClick={() => setIsMenuOpen(false)}
        style={{ pointerEvents: isMenuOpen ? "auto" : "none" }}
      />

      <aside
        ref={drawerRef}
        className={styles.mobileDrawer}
        aria-hidden={!isMenuOpen}
        style={{
          pointerEvents: isMenuOpen ? "auto" : "none",
          visibility: isMenuOpen ? "visible" : "hidden",
          display: isMenuOpen ? "block" : "none",
        }}
      >
        <div className={styles.drawerHeader}>
          <NavLink to="/" className={styles.brand} aria-label="MIT4Doctors home">
            <img
              src="/img/logo/mit-logo-w.webp"
              alt="MIT4Doctors"
              className={styles.brandLogo}
            />
          </NavLink>

          <button
            type="button"
            className={styles.drawerClose}
            aria-label="Close navigation"
            onClick={() => setIsMenuOpen(false)}
          >
            <FiX size={20} />
          </button>
        </div>

        <nav aria-label="Mobile navigation" className={styles.mobileNavLinks}>
          {headerNavigation.map((item, index) => {
            if (item.label === "Services") {
              return (
                <div
                  key={item.label}
                  className={styles.mobileServiceGroup}
                  ref={(element) => {
                    mobileLinkRefs.current[index] = element;
                  }}
                >
                  <button
                    type="button"
                    className={styles.mobileNavTrigger}
                    onClick={toggleMobileServices}
                    aria-expanded={isMobileServicesOpen}
                  >
                    <span>{item.label}</span>
                    <FiChevronDown aria-hidden="true" />
                  </button>

                  <div
                    className={[
                      styles.mobileServiceList,
                      isMobileServicesOpen ? styles.mobileServiceListOpen : "",
                    ].join(" ")}
                  >
                    <NavLink
                      to={toRoutePath("/services")}
                      className={styles.mobileServiceAll}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      View All Services
                    </NavLink>
                    {serviceDropdownItems.map((service) => (
                      <NavLink
                        key={service.title}
                        to={toRoutePath(service.link)}
                        className={styles.mobileServiceLink}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <span>{service.title}</span>
                      </NavLink>
                    ))}
                  </div>
                </div>
              );
            }

            return (
              <NavLink
                key={item.to}
                to={toRoutePath(item.to)}
                end={item.to === "/"}
                ref={(element) => {
                  mobileLinkRefs.current[index] = element;
                }}
                className={({ isActive }) =>
                  `${styles.mobileNavLink} ${isActive ? styles.mobileNavLinkActive : ""}`
                }
                onClick={() => setIsMenuOpen(false)}
              >
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <NavLink
          to={toRoutePath("/contact-us")}
          className={`btn ${styles.mobileAppointment}`}
          onClick={() => setIsMenuOpen(false)}
        >
          <FiCalendar aria-hidden="true" />
          <span>Appointment</span>
        </NavLink>
      </aside>
    </header>
  );
}
